import { FC, useMemo } from 'react';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Button, Skeleton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { ScalarLineChart } from 'components/Charts';
import {
  ChangeLabel,
  CurrentValue,
  HeaderLeft,
  MarksTable,
  MarksTableCell,
  MarksTableHead,
  MarksTableRow,
  SeriesChip,
  SeriesToggleRow,
  ViewToggle,
} from './ZanbatoReportedMarksCard.styles';
import {
  buildChartSeries,
  computeChange,
  filterMarksByPeriod,
  formatCurrency,
  formatPeriodLabel,
  getLatestMedian,
  hasAnyStat,
  Period,
  PERIODS,
  PeriodValue,
  SERIES_CONFIGS,
  toNum,
  ZanbatoReportedMarkItem,
  ZanbatoReportedMarksResponse,
} from './ZanbatoReportedMarksCard.utils';
import { CardHeaderRow } from '../MutualFundMarksCard/MutualFundMarksCard.styles';
import {
  EndDateMode,
  EndDateModeValue,
  formatChartDate,
  ViewMode,
  ViewModeValue,
} from '../MutualFundMarksCard/MutualFundMarksCard.utils';

interface Props {
  data: ZanbatoReportedMarksResponse | undefined;
  isLoading: boolean;
  measurementDate?: string;
  endDateMode: EndDateModeValue;
  view: ViewModeValue;
  onViewChange: (v: ViewModeValue) => void;
  period: PeriodValue;
  onPeriodChange: (v: PeriodValue) => void;
  enabledIds: Set<string>;
  onEnabledIdsChange: (ids: Set<string>) => void;
}

const ZanbatoMarksBody: FC<Props> = ({
  data,
  isLoading,
  measurementDate,
  endDateMode,
  view,
  onViewChange,
  period,
  onPeriodChange,
  enabledIds,
  onEnabledIdsChange,
}) => {
  const marks = useMemo<ZanbatoReportedMarkItem[]>(() => data?.marks ?? [], [data?.marks]);

  // Ignore placeholder rows (report_period set, every stat null) — Zanbato returns
  // these for recent months whose filings haven't landed yet.
  const latestDataDate = useMemo(() => {
    const dated = marks.filter(m => m.report_period && hasAnyStat(m));
    if (!dated.length) return undefined;
    return [...dated].sort((a, b) => b.report_period!.localeCompare(a.report_period!))[0].report_period ?? undefined;
  }, [marks]);

  const effectiveEndDate = useMemo(
    () => (endDateMode === EndDateMode.Measurement && measurementDate ? measurementDate : latestDataDate),
    [endDateMode, measurementDate, latestDataDate],
  );

  const filteredMarks = useMemo(
    () => filterMarksByPeriod(marks, period, effectiveEndDate),
    [marks, period, effectiveEndDate],
  );

  const series = useMemo(() => buildChartSeries(filteredMarks, enabledIds), [filteredMarks, enabledIds]);
  const { value: latestMedian, period: latestMedianPeriod } = useMemo(
    () => getLatestMedian(marks, effectiveEndDate),
    [marks, effectiveEndDate],
  );
  const change = useMemo(() => computeChange(filteredMarks), [filteredMarks]);

  const hasData = marks.length > 0;

  const toggleSeries = (id: string) => {
    const next = new Set(enabledIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onEnabledIdsChange(next);
  };

  return (
    <>
      <CardHeaderRow>
        <HeaderLeft>
          {hasData && latestMedian !== null && (
            <>
              <CurrentValue>{formatCurrency(latestMedian)}</CurrentValue>
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                median{latestMedianPeriod ? ` · ${formatPeriodLabel(latestMedianPeriod)}` : ''}
              </Typography>
              {change && (
                <ChangeLabel positive={change.abs >= 0}>
                  {change.abs >= 0 ? '+' : ''}{change.abs.toFixed(2)} ({change.abs >= 0 ? '+' : ''}{change.pct.toFixed(2)}%)
                </ChangeLabel>
              )}
              {latestDataDate && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontStyle: 'italic' }}>
                  Data through {formatPeriodLabel(latestDataDate)}
                </Typography>
              )}
            </>
          )}
        </HeaderLeft>
        <ViewToggle>
          {hasData && (
            <ToggleButtonGroup
              value={period}
              exclusive
              size="small"
              onChange={(_e, v: PeriodValue | null) => {
                if (v) {
                  onPeriodChange(v);
                }
              }}
            >
              {PERIODS.map(p => (
                <ToggleButton key={p} value={p}>{p}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
          <ToggleButtonGroup
            value={view}
            exclusive
            size="small"
            onChange={(_e, v: ViewModeValue | null) => {
              if (v) {
                onViewChange(v);
              }
            }}
          >
            <ToggleButton value={ViewMode.Chart}>Chart</ToggleButton>
            <ToggleButton value={ViewMode.Table}>Table</ToggleButton>
          </ToggleButtonGroup>
          <Button
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon sx={{ fontSize: '0.7rem !important' }} />}
            href="https://zanbato.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'none', height: 26, py: 0 }}
          >
            Zanbato
          </Button>
        </ViewToggle>
      </CardHeaderRow>

      {isLoading && (
        <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 1 }} />
      )}

      {!isLoading && !hasData && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
          No Zanbato reported marks available for this company.
        </Typography>
      )}

      {!isLoading && hasData && (
        <>
          <SeriesToggleRow>
            {SERIES_CONFIGS.map(s => (
              <SeriesChip
                key={s.id}
                label={s.label}
                size="small"
                variant="outlined"
                active={enabledIds.has(s.id)}
                seriesColor={s.color}
                onClick={() => toggleSeries(s.id)}
              />
            ))}
          </SeriesToggleRow>

          {view === ViewMode.Chart && (
            <ScalarLineChart
              series={series}
              notMerge
              smooth
              showPoints
              style={{ height: 280 }}
              config={{
                legend: { show: true, position: 'top' },
                xAxis: { formatter: v => formatPeriodLabel(String(v)) },
                yAxis: { formatter: v => `$${v}` },
                options: {
                  tooltip: {
                    trigger: 'axis',
                    appendToBody: true,
                    formatter: (params: any) => {
                      if (!Array.isArray(params) || !params.length) return '';
                      const dateLabel = formatChartDate(String(params[0].axisValue));
                      return (
                        `<div style="font-weight:700;margin-bottom:4px;font-size:0.82rem">${dateLabel}</div>${params
                          .filter((p: any) => p.value != null)
                          .map((p: any) => `<div>${p.marker}${p.seriesName}: <b>$${Number(p.value).toFixed(2)}</b></div>`)
                          .join('')}`
                      );
                    },
                  },
                },
              }}
            />
          )}

          {view === ViewMode.Table && (
            <MarksTable>
              <thead>
                <MarksTableRow>
                  <MarksTableHead>Period</MarksTableHead>
                  <MarksTableHead align="right">Median</MarksTableHead>
                  <MarksTableHead align="right">Wtd. Avg</MarksTableHead>
                  <MarksTableHead align="right">Simple Avg</MarksTableHead>
                  <MarksTableHead align="right">Max</MarksTableHead>
                  <MarksTableHead align="right">Min</MarksTableHead>
                </MarksTableRow>
              </thead>
              <tbody>
                {[...filteredMarks]
                  .sort((a, b) => (b.report_period ?? '').localeCompare(a.report_period ?? ''))
                  .map((m, i) => (
                    <MarksTableRow key={`${m.report_period}-${i}`}>
                      <MarksTableCell>
                        {m.report_period ? formatPeriodLabel(m.report_period) : '—'}
                      </MarksTableCell>
                      <MarksTableCell align="right">{formatCurrency(toNum(m.reported_marks_median))}</MarksTableCell>
                      <MarksTableCell align="right">{formatCurrency(toNum(m.reported_marks_dollar_weighted_average))}</MarksTableCell>
                      <MarksTableCell align="right">{formatCurrency(toNum(m.reported_marks_simple_average))}</MarksTableCell>
                      <MarksTableCell align="right">{formatCurrency(toNum(m.reported_marks_max))}</MarksTableCell>
                      <MarksTableCell align="right">{formatCurrency(toNum(m.reported_marks_min))}</MarksTableCell>
                    </MarksTableRow>
                  ))}
              </tbody>
            </MarksTable>
          )}
        </>
      )}
    </>
  );
};

export { ZanbatoMarksBody };
